import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import React, { FC, useCallback, useEffect, useState } from "react";
import { GridToolbarContainer } from "@material-ui/data-grid";

import BaseDataTable from "../../../../../components/modules/BaseDataTable";
import Button from "../../../../../components/elements/Button";
import CreativeAssetsManagement from "./CreativeAssetsManagement";
import LandingPageEditMode from "./LandingPageEditMode";
import LandingPageViewMode from "./LandingPageViewMode";
import UploadCreativesModal from "./UploadCreativesModal";
import UploadFromTemplateModal from "./UploadFromTemplateModal";
import api from "../../../../../api";
import classes from "../../CampaignsDashboard.module.scss";
import { AdGroup, CreativeAsset } from "../../../../../types/adGroupTypes";
import { CampaignType } from "../../../../../types/campaignTypes";
import { errorToaster } from "../../../../../helpers/toasters";
import { truncateText } from "../../../../../helpers/truncateText";

type AdGroupsCreativesMap = Record<string, CreativeAsset[]>;

type AdgroupsDashboardProps = {
  selectedCampaign: CampaignType;
};

const fetchAdGroupCreatives = async (adGroupId: string) => {
  try {
    const adGroupCreatives = await api.getCreativesByAdGroup(adGroupId);
    return adGroupCreatives;
  } catch (error) {
    const err = error as any;
    throw new Error(err.message);
  }
};

const fetchAdGroup = async (campaignId: string) => {
  try {
    const adGroups = await api.getAdGroupByCampaignId(campaignId);
    return adGroups;
  } catch (error) {
    const err = error as any;
    throw new Error(err.message);
  }
};

const AdGroupsCreativesDashboard: FC<AdgroupsDashboardProps> = ({ selectedCampaign }) => {
  const [adGroupsCreativesMap, setAdGroupsCreativesMap] = useState<AdGroupsCreativesMap>({});
  const [adGroup, setAdGroup] = useState<AdGroup>();
  const [isFetchingCreatives, setIsFetchingCreatives] = useState<boolean>(false);
  const [isAdGroupsPending, setIsAdGroupsPending] = useState<boolean>(false);

  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [isOpenModalTemplates, setIsOpenModalTemplates] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [disabled, setDisabled] = useState<boolean>(false);

  const [landingPage, setLandingPage] = useState<string>("");
  const [fetchedAdGroupId, setFetcheAdGroupId] = useState<string>("");

  const columns = [
    { key: "name", label: "Name", minWidth: 170 },
    { key: "createdBy", label: "Created By", minWidth: 170 },
    { key: "createdDate", label: "Created Date", minWidth: 170 },
    { key: "addNewCreatives", label: "", minWidth: 100 },
    // { key: "uploadSavedCreatives", label: "", minWidth: 100 },
  ] as const;

  const resetStates = () => {
    setFetcheAdGroupId("");
    setAdGroup(undefined);
    setIsAdGroupsPending(false);
  };

  const fetchAndSetAdGroup = useCallback(async () => {
    try {
      setIsAdGroupsPending(true);
      const { payload: fetchedAdGroup } = await fetchAdGroup(selectedCampaign.brandgenCampaignId);

      if (!fetchedAdGroup) {
        resetStates();
        return;
      }
      setFetcheAdGroupId(fetchedAdGroup.adGroupId);
      setAdGroup(fetchedAdGroup);
      setIsAdGroupsPending(false);
    } catch (error) {
      setIsAdGroupsPending(false);
      const err = error as any;
      throw new Error(err.message);
    }
  }, [selectedCampaign]);

  const fetchAndSetCreatives = useCallback(async () => {
    try {
      if (!fetchedAdGroupId) {
        resetStates();
        return;
      }
      setIsFetchingCreatives(true);
      const { payload: fetchedAdGroupCreatives } = await fetchAdGroupCreatives(fetchedAdGroupId);

      if (!fetchedAdGroupCreatives) {
        resetStates();
        return;
      }

      setAdGroupsCreativesMap({
        [fetchedAdGroupId]: fetchedAdGroupCreatives.creatives,
      });

      if (fetchedAdGroupCreatives.creatives.length !== 0) {
        setLandingPage(fetchedAdGroupCreatives.creatives[0].click_url);
      } else {
        setLandingPage("");
      }
      setIsFetchingCreatives(false);
    } catch (error) {
      setIsAdGroupsPending(false);
      setIsFetchingCreatives(false);

      const err = error as any;
      throw new Error(err.message);
    }
  }, [fetchedAdGroupId]);

  useEffect(() => {
    try {
      if (selectedCampaign.brandgenCampaignId) {
        fetchAndSetAdGroup();
        fetchAndSetCreatives();
      }
    } catch (error) {
      const err = error as any;
      errorToaster(`There was an error: ${err.message}`);
    }
  }, [selectedCampaign, fetchAndSetAdGroup, fetchAndSetCreatives]);

  useEffect(() => {
    try {
      if (
        adGroupsCreativesMap &&
        adGroup &&
        adGroupsCreativesMap[adGroup.adGroupId]?.length > 0 &&
        adGroupsCreativesMap[adGroup.adGroupId][0]?.creativeType === "video/mp4"
      ) {
        setDisabled(true);
      } else {
        setDisabled(false);
      }
    } catch (error) {
      const err = error as any;
      errorToaster(`There was an error: ${err.message}`);
    }
  }, [adGroupsCreativesMap, adGroup]);

  const handleAdGroupExpand = async (adGroupId: string) => {
    try {
      const adGroupCreatives = adGroupsCreativesMap[adGroupId];
      if (!adGroupCreatives) {
        fetchAndSetCreatives();
      }
      return adGroupCreatives;
    } catch (error) {
      const err = error as any;
      setIsFetchingCreatives(false);
      errorToaster(`There was an error: ${err.message}`);
      return null;
    }
  };

  const handleChangeOnEditMode = () => {
    setIsEditMode(true);
  };

  const handleChangeOnViewMode = () => {
    setIsEditMode(false);
  };

  const rows = [
    adGroup
      ? {
          id: adGroup.adGroupId,
          name: (() => (
            <Tooltip title={adGroup.name}>
              <Typography>{truncateText(adGroup.name, 36)}</Typography>
            </Tooltip>
          ))(),
          createdBy: (() => (
            <Tooltip title={adGroup.createdBy}>
              <Typography>{truncateText(adGroup.createdBy, 36)}</Typography>
            </Tooltip>
          ))(),
          createdDate: new Date(adGroup.createdAt).toLocaleDateString(),
          addNewCreatives: (() => (
            <Button onClick={() => setIsOpenModal(true)} variant="contained" disabled={disabled}>
              Upload New AdGroup
            </Button>
          ))(),
          // uploadSavedCreatives: (() => (
          //   <Button onClick={() => setIsOpenModalTemplates(true)} variant="contained" disabled={disabled}>
          //     Upload From Templates
          //   </Button>
          // ))(),
          collapsableConfigs: {
            collapsable: true,
            collapsableChild: (
              <CreativeAssetsManagement
                isFetchingCreatives={isFetchingCreatives}
                creativeAssets={adGroupsCreativesMap[adGroup.adGroupId] ?? []}
              />
            ),
            expandControlHandler: () => {
              handleAdGroupExpand(adGroup.adGroupId);
            },
          },
        }
      : {
          id: "",
          name: "",
          createdBy: "",
          createdDate: "",
          addNewCreatives: "",
          // uploadSavedCreatives: "",
        },
  ];

  return (
    <div className={classes.dashboardContainer}>
      <GridToolbarContainer className={classes.toolbar}>
        <Typography variant="h3">Campaign Creative List</Typography>
        {isEditMode ? (
          <LandingPageEditMode
            landingPage={landingPage}
            changeMode={handleChangeOnViewMode}
            adGroupId={adGroup?.adGroupId}
            fetchAndSetCreatives={fetchAndSetCreatives}
          />
        ) : (
          <LandingPageViewMode landingPage={landingPage} changeMode={handleChangeOnEditMode} />
        )}
      </GridToolbarContainer>
      {isAdGroupsPending ? (
        <Typography className={classes.loaderLabel}>Please, wait while we get everything in order...</Typography>
      ) : !isAdGroupsPending && adGroup === undefined ? (
        <Typography className={classes.noAdGroupsText}>
          Unfortunately there is no information about Ad Groups...
        </Typography>
      ) : (
        <BaseDataTable columns={columns} rows={rows} />
      )}

      <UploadCreativesModal
        isOpen={isOpenModal}
        handleClose={() => setIsOpenModal(false)}
        beeswaxAdvertiserId={selectedCampaign.beeswaxAdvertiserId}
        brandgenAdvertiserId={selectedCampaign.brandgenAdvertiserId}
        landingPage={landingPage}
        fetchedAdGroupId={fetchedAdGroupId}
        fetchAndSetAdGroup={fetchAndSetAdGroup}
        brandgenCampaignId={selectedCampaign.brandgenCampaignId}
        fetchedCreativesAssets={adGroupsCreativesMap[fetchedAdGroupId] ?? []}
      />
      <UploadFromTemplateModal
        isOpen={isOpenModalTemplates}
        handleClose={() => setIsOpenModalTemplates(false)}
        beeswaxAdvertiserId={selectedCampaign.beeswaxAdvertiserId}
        brandgenAdvertiserId={selectedCampaign.brandgenAdvertiserId}
        landingPage={landingPage}
        fetchedAdGroupId={fetchedAdGroupId}
        fetchAndSetAdGroup={fetchAndSetAdGroup}
        brandgenCampaignId={selectedCampaign.brandgenCampaignId}
        fetchedCreativesAssets={adGroupsCreativesMap[fetchedAdGroupId] ?? []}
      />
    </div>
  );
};

export default AdGroupsCreativesDashboard;
